// The update notice (06-service-worker.md): a new version has taken over; desyr never reloads by
// itself for an update, so a half-written message is never lost. In the top layer, above dialogs, and
// inside the open modal window (settings), which would otherwise make it inert. It names the new
// release's version from its /version.json, read when it appears; on a phone the page leaves room
// for it at the bottom (--update-room; v1.58.10).
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import logo from '../assets/logo.svg';
import { IconButton } from '../blocks/Button';
import { useTopModal } from '../blocks/Dialog';
import { icons } from '../blocks/icons';
import { reloadForUpdate, useUpdateReady } from '../core/worker';
import { t } from '../i18n';

export function UpdateNotice() {
  const ready = useUpdateReady();
  const [later, setLater] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [version, setVersion] = useState<string>();
  const ref = useRef<HTMLDivElement>(null);
  const shown = ready && !later;
  const layer = useTopModal(shown);
  useLayoutEffect(() => {
    const el = ref.current;
    if (shown && el?.isConnected && !el.matches(':popover-open')) el.showPopover?.();
  });
  useEffect(() => {
    if (!ready) return;
    fetch('/version.json', { cache: 'no-store' })
      .then((r) => r.json())
      .then((j) => typeof j?.version === 'string' && setVersion(j.version))
      .catch(() => {});
  }, [ready]);
  useEffect(() => {
    const el = ref.current;
    if (!shown || !el) return;
    const root = document.documentElement.style;
    const room = new ResizeObserver(() => root.setProperty('--update-room', `${innerHeight - el.getBoundingClientRect().top + 8}px`));
    room.observe(el);
    return () => {
      room.disconnect();
      root.removeProperty('--update-room');
    };
  }, [shown]);
  if (!shown) return null;
  const notice = (
    <div ref={ref} className="update-notice" role="status" popover="manual" data-desyr-update-notice="">
      <img src={logo} alt="" width={32} height={32} />
      <div className="update-text">
        <strong>{t(updating ? 'update.updating' : 'update.title')}</strong>
        <span>{updating ? t('update.moment') : version ? t('update.lineVersion', { version }) : t('update.line')}</span>
      </div>
      <button
        type="button"
        className="update-reload"
        disabled={updating}
        onClick={() => {
          setUpdating(true);
          reloadForUpdate();
        }}
      >
        {t(updating ? 'update.busy' : 'common.reload')}
      </button>
      {!updating && <IconButton label={t('common.later')} icon={icons.close} size={20} onClick={() => setLater(true)} />}
    </div>
  );
  return layer ? createPortal(notice, layer) : notice;
}
